import React, { useEffect, useState } from "react";
import TableWithSkeleton from "../../../components/global/table/TableWithSkeleton";
import Pagination from "../../../components/global/pagination/Pagination";
import columns from "../../../columns/settings/lpConfig/columns";
import ModalComponent from "../../../components/global/modal/ModalComponent";
import { Input, Form, Switch, InputNumber } from "antd";
import LoadableButton from "../../../components/buttons/LoadableButton";
import {
  AddUpdateLpConfigApi,
  DeleteLpConfigApi,
  GetAllLpConfigApi,
} from "../../../api/request/settings/lpConfig";
import moment from "moment/moment";
import toast from "react-hot-toast";
import ConfirmationModal from "../../../components/global/modal/ConfirmationModal";
import Edit from "../../../assets/Edit";
import { useSelector } from "react-redux";

const LpConfig = () => {
  const [form] = Form.useForm();

  const userDetails = useSelector((state) => state.user.userDetails);
  let isSalesManager = userDetails?.roleId === 6;

  const [isLoading, setIsLoading] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [isAddEditModal, setIsAddEditModal] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);
  const [data, setData] = useState([]);
  const [lpConfigId, setLpConfigId] = useState("");
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const initialValues = { loginId: "", name: "", isActive: true };

  const handleEdit = (lpConfig) => {
    setIsAddEditModal(true);
    setIsAdd(false);
    setLpConfigId(lpConfig.id);
    form.setFieldsValue(lpConfig);
  };

  const getAllLpConfig = async () => {
    setIsLoading(true);
    try {
      const { data } = await GetAllLpConfigApi();
      if (data?.success) {
        setData(
          data?.data?.map((item) => {
            return {
              ...item,
              isActive: (
                <Switch
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                  checked={item?.isActive}
                  loading={item?.id === lpConfigId && isStatusLoading}
                  onChange={() =>
                    handleStatusChange({
                      id: item?.id,
                      loginId: item?.loginId,
                      name: item?.name,
                      isActive: !item?.isActive,
                    })
                  }
                />
              ),
              createdDate: item?.createdDate
                ? moment(item?.createdDate).format("DD-MM-YYYY, hh:mm A")
                : "--",
              actions: (
                <div className="flex items-center gap-3">
                  <button onClick={() => handleEdit(item)}>
                    <Edit />
                  </button>
                  <button
                    onClick={() => {
                      setIsDelete(true);
                      setLpConfigId(item?.id);
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="#D91819"
                      className="w-6 h-6 cursor-pointer hover:stroke-[#aa0001] duration-500"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                      />
                    </svg>
                  </button>
                </div>
              ),
            };
          }),
        );
        setTotalRecord(data?.totalRecords);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setIsSubmitLoading(true);
    try {
      const payload = isAdd ? values : { ...values, id: lpConfigId };
      const { data } = await AddUpdateLpConfigApi(payload);
      if (data?.success) {
        getAllLpConfig();
        form.resetFields();
        setIsAddEditModal(false);
        toast.success(data?.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error?.message);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleStatusChange = async (values) => {
    setLpConfigId(values?.id);
    setIsStatusLoading(true);
    try {
      const { data } = await AddUpdateLpConfigApi(values);
      if (data?.success) {
        toast.success(data?.message);
        getAllLpConfig();
      }
    } catch (error) {
      toast.error(error?.response?.data?.error?.message);
    } finally {
      setIsStatusLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setIsDeleteLoading(true);
    try {
      const { data } = await DeleteLpConfigApi(id);
      if (data?.success) {
        toast.success(data?.message);
        setIsDelete(false);
        getAllLpConfig();
      }
    } catch (error) {
      toast.error(error?.response?.data?.error?.message);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  useEffect(() => {
    getAllLpConfig();
  }, [page, pageSize]);

  useEffect(() => {
    if (!isAddEditModal) {
      form.resetFields();
      setIsAddEditModal(false);
    }
  }, [isAddEditModal]);

  return (
    <>
      <div>
        <div className="flex justify-end items-center">
          {!isSalesManager && (
            <button
              onClick={() => {
                setIsAdd(true);
                setIsAddEditModal(true);
              }}
              className="flex items-center bg-primary text-sm text-white font-semibold px-6 py-2 rounded-lg themeHover  duration-500"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                className="mr-2"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.85714 9.14286H0V6.85714H6.85714V0H9.14286V6.85714H16V9.14286H9.14286V16H6.85714V9.14286Z"
                  fill="white"
                />
              </svg>
              Add
            </button>
          )}
        </div>

        <div className="my-6">
          <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
            <TableWithSkeleton
              columns={columns}
              data={data}
              loading={isLoading}
              hiddenColumns={isSalesManager ? ["Actions", "Status"] : []}
              page={page}
              pageSize={pageSize}
              index={true}
            />
          </div>

          <Pagination
            total={totalRecord}
            pageSize={pageSize}
            setPageSize={setPageSize}
            page={page}
            setPage={setPage}
          />
        </div>
      </div>

      {/* Add Edit modal */}
      <ModalComponent
        isOpen={isAddEditModal}
        setIsOpen={setIsAddEditModal}
        title={isAdd ? "Add LP Config" : "Edit LP Config"}
      >
        <Form
          className="w-full space-y-5"
          autoComplete="off"
          form={form}
          initialValues={initialValues}
          onFinish={handleSubmit}
        >
          <div className="input-white">
            <label className="text-description text-[#2F2B3DCC] font-medium">
              Login ID
            </label>
            <Form.Item
              name="loginId"
              rules={[{ required: true, message: "Please enter login id" }]}
            >
              <InputNumber
                type="number"
                placeholder="Login id"
                className="mt-1 w-full py-1"
                controls={false}
              />
            </Form.Item>
          </div>

          <div className="input-white">
            <label className="text-description  text-[#2F2B3DCC] font-medium">
              Name
            </label>
            <Form.Item
              name="name"
              rules={[{ required: true, message: "Please enter name" }]}
            >
              <Input placeholder="Name" className="mt-1 w-full ps-4 py-2" />
            </Form.Item>
          </div>

          <div className="input-white">
            <label className="text-description  text-[#2F2B3DCC] font-medium">
              Status
            </label>
            <Form.Item name="isActive">
              <Switch checkedChildren="ON" unCheckedChildren="OFF" />
            </Form.Item>
          </div>

          <div className="flex justify-center items-center mt-7 gap-5">
            <button
              type="button"
              onClick={() => setIsAddEditModal(false)}
              className="bg-primary text-title text-white font-medium uppercase px-10 py-2 rounded-lg hover:bg-primary duration-500"
            >
              Back
            </button>
            <LoadableButton
              type="submit"
              className="bg-primary text-title text-white font-medium uppercase px-10 py-2 rounded-lg hover:bg-primary duration-500"
              lable={isAdd ? "Submit" : "Update"}
              isLoading={isSubmitLoading}
              loadingLable={isAdd ? "Submitting..." : "Updating..."}
            />
          </div>
        </Form>
      </ModalComponent>

      {/* delete modal */}
      <ConfirmationModal
        isOpen={isDelete}
        setIsOpen={setIsDelete}
        message="Are you sure, you want to delete ?"
        isLoading={isDeleteLoading}
        loadingLabel="Removing..."
        onConfirm={() => handleDelete(lpConfigId)}
      />
    </>
  );
};

export default LpConfig;
